import React, { PureComponent, Suspense } from "react";
import PropTypes from "prop-types";
import styled, { createGlobalStyle } from "styled-components";
import { withTranslation } from "react-i18next";
import { cssMainSelector } from "../lib/cssSelectors";
import MaterialIcon from "./MaterialIcon";
import { MODALS } from "../constants/modals";
import { darken } from "polished";
import { CSS_CLASS_PREFIX } from "../constants/css";
import { UNLIMITED } from "../constants/unlimited";

function modals(type) {
  switch (type) {
    case MODALS.filterModal:
      return FilterModal;
    case MODALS.phonePickerModal:
      return PhonePickerModal;
    case MODALS.shopsModal:
      return ShopsModal;
    case MODALS.tcoModal:
      return TCOModal;
    default:
      return null;
  }
}

const FilterModal = React.lazy(() => import("./modals/FilterModal"));
const PhonePickerModal = React.lazy(() => import("./modals/PhonePickerModal"));
const ShopsModal = React.lazy(() => import("./modals/ShopsModal"));
const TCOModal = React.lazy(() => import("./modals/TCOModal"));

// When the modal opens, a class is set on the body to stop it from scrolling
const GlobalStyles = createGlobalStyle`
  body.${CSS_CLASS_PREFIX}-modal-open {
    overflow: hidden;
  }
`;

const Container = styled.div``;

const Overlay = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.color.black};
    bottom: 0;
    left: 0;
    opacity: 0.5;
    position: fixed;
    right: 0;
    top: 0;
    z-index: ${UNLIMITED};
  }
`;

const OuterWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    bottom: 0;
    left: 0;
    overflow-x: hidden;
    overflow-y: auto;
    position: fixed;
    right: 0;
    top: 0;
    z-index: ${UNLIMITED};
  }
`;

const FlexWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
  }
`;

const AboveCloser = styled.div`
  flex: 0 1 20%;
  order: 1;
`;

const BelowCloser = styled.div`
  flex: 1 1 auto;
  order: 3;
`;

const MiddleFlexWrapper = styled.div`
  display: flex;
  flex: 0 0 auto;
  justify-content: space-between;
  order: 2;
`;

const LeftSideCloser = styled.div`
  flex: 1 1 auto;
  order: 1;
`;

const ContentWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.modal.backgroundColor};
    border-radius: ${p => p.theme.generic.borderRadius};
    flex: 0 0 auto;
    margin: auto;
    order: 2;
    position: relative;
  }
`;

const RightSideCloser = styled.div`
  flex: 1 1 auto;
  order: 3;
`;

const ModalWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    padding: ${p => p.theme.spacing.md};
  }
`;

const CloseX = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.modal.closeButtonsBackgroundColor};
    border-radius: ${p => p.theme.generic.borderRadius};
    color: ${p => p.theme.modal.textColor};
    cursor: pointer;
    display: block;
    height: 35px;
    position: absolute;
    right: 0px;
    text-align: center;
    top: 0px;
    user-select: none;
    width: 35px;

    &:hover,
    &:focus {
      background-color: ${p =>
        darken(0.05, p.theme.modal.closeButtonsBackgroundColor)};
    }

    .${CSS_CLASS_PREFIX}-material-icon {
      color: ${p => p.theme.modal.textColor};
      font-size: 24px;
      line-height: 35px;
      vertical-align: middle;
    }
  }
`;

const CloseButton = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.modal.closeButtonsBackgroundColor};
    border-radius: ${p => p.theme.generic.borderRadius};
    color: ${p => p.theme.modal.textColor};
    cursor: pointer;
    display: block;
    height: 35px;
    line-height: 35px;
    text-align: center;
    user-select: none;
    vertical-align: middle;
    width: 100%;

    &:hover,
    &:focus {
      background-color: ${p =>
        darken(0.1, p.theme.modal.closeButtonsBackgroundColor)};
    }
  }
`;

class Modal extends PureComponent {
  render() {
    const {
      clientConfig,
      data,
      offerType,
      portal,
      resetModal,
      t,
      type,
      updateExperiment,
      updateModal
    } = this.props;
    const SelectedModal = modals(type);

    if (type == null || data == null) {
      return null;
    } else {
      return (
        <Container>
          <GlobalStyles />
          <Overlay />
          <OuterWrapper>
            <FlexWrapper>
              <AboveCloser onClick={resetModal} />
              <MiddleFlexWrapper>
                <LeftSideCloser onClick={resetModal} />
                <ContentWrapper>
                  <CloseX onClick={resetModal}>
                    <MaterialIcon>close</MaterialIcon>
                  </CloseX>
                  <ModalWrapper>
                    {type !== null && (
                      <Suspense fallback={<div> </div>}>
                        <SelectedModal
                          clientConfig={clientConfig}
                          data={data}
                          offerType={offerType}
                          portal={portal}
                          resetModal={resetModal}
                          updateExperiment={updateExperiment}
                          updateModal={updateModal}
                        />
                      </Suspense>
                    )}
                  </ModalWrapper>
                  <CloseButton onClick={resetModal}>
                    {t("all.close")}
                  </CloseButton>
                </ContentWrapper>
                {type !== MODALS.filterModal && (
                  <RightSideCloser onClick={resetModal} />
                )}
              </MiddleFlexWrapper>
              <BelowCloser onClick={resetModal} />
            </FlexWrapper>
          </OuterWrapper>
        </Container>
      );
    }
  }
}

Modal.propTypes = {
  clientConfig: PropTypes.object.isRequired,
  data: PropTypes.object,
  offerType: PropTypes.string.isRequired,
  portal: PropTypes.object.isRequired,
  resetModal: PropTypes.func,
  type: PropTypes.string,
  updateExperiment: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  updateModal: PropTypes.func
};

Modal.defaultProps = {
  data: null,
  type: null
};

export default withTranslation(["modal"])(Modal);
